import { useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { DetailsDisplay } from './DetailsDisplay';
import { getTitle, subTitle } from './detailsDrawersHelpers';
import { UpstackDrawer } from '../Drawer/Drawer';
import { DrawerData, DrawerState, getDrawerOpenSubject, getDrawersSubject } from '../../../store/Drawer';
import { ComponentSpinner } from '../Loading/ComponentSpinner';

const keyOf = (record: DrawerData | undefined, idKey: string) =>
  (record as Record<string, string | number | undefined> | undefined)?.[idKey];

// Detail subjects outlive the drawer, so they can replay a different record than the one just opened.
const isSameRecord = (drawer: DrawerState | undefined, update: DrawerData) => {
  if (!drawer?.idKey) return true;
  const openKey = keyOf(drawer.data, drawer.idKey);
  const updateKey = keyOf(update, drawer.idKey);
  return openKey !== undefined && (updateKey === undefined || String(openKey) === String(updateKey));
};

export function DetailsDrawer() {
  const [drawers, setDrawers] = useState<DrawerState[]>([]);
  const [drawerData, setDrawerData] = useState<DrawerState | undefined>();
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const location = useLocation();

  const latestLoad = useRef(0);

  const loadDrawer = async (drawer: DrawerState | undefined) => {
    const load = ++latestLoad.current;
    if (!drawer?.fetchFunction) return setLoading(false);
    setLoading(true);
    const data = await drawer.fetchFunction().catch(() => undefined);
    if (load !== latestLoad.current) return;
    if (data && drawer.updateSubject) drawer.updateSubject(data);
    setDrawerData(data ? { ...drawer, data } : drawer);
    setLoading(false);
  };

  useEffect(() => {
    const drawerStateSub = getDrawersSubject().subscribe((s) => {
      setDrawers(s);
      setDrawerData(s?.[0]);
    });
    const drawerOpenSub = getDrawerOpenSubject().subscribe((drawerOpen) => setShowDrawer(drawerOpen));
    return () => {
      if (drawerStateSub) drawerStateSub.unsubscribe();
      if (drawerOpenSub) drawerOpenSub.unsubscribe();
    };
  }, []);

  // Reload on every stack change: callers reuse drawer objects and some open drawers with an empty id.
  useEffect(() => {
    const openDrawer = drawers[0];
    loadDrawer(openDrawer);

    const dataSub = openDrawer?.dataSubject?.().subscribe((d) => {
      if (d !== undefined) {
        setDrawerData((prev) => {
          if (!isSameRecord(prev, d)) return prev;
          return { ...prev, data: { ...(prev?.data || {}), ...d } } as DrawerState;
        });
      }
    });

    return () => dataSub?.unsubscribe();
  }, [drawers]);

  if (!drawerData) return <div></div>;

  const state = {
    data: drawerData,
    prevpath: location.pathname,
    title: drawerData?.title,
    componentName: drawerData?.componentName
  };

  return (
    <UpstackDrawer
      id={drawerData.id}
      data-testid="details-drawer"
      show={showDrawer}
      title={drawerData.title || getTitle(drawerData.data, drawerData.titleKey)}
      subtitle={subTitle(drawerData.componentName, drawerData.data, drawerData.subtitleKey || '')}
      showButtons={drawerData.showButtons}
      link={drawerData.link}
      showIcon={drawerData.showIcon}
      componentName={drawerData.componentName}
      state={state}
      showExpand={drawerData.showExpand}
      fullPageLink={drawerData.fullPageLink}
      linkGroup={drawerData.linkGroup}
      showArchived={drawerData.showArchived}
      key={drawerData.id}>
      <>
        {loading && (
          <div className="flex h-full m-auto">
            <ComponentSpinner label="Loading data..." />
          </div>
        )}
        {!loading && (
          <DetailsDisplay
            data={drawerData.data}
            componentName={drawerData.componentName}
            deleteAction={drawerData.deleteAction}
          />
        )}
      </>
    </UpstackDrawer>
  );
}
